<script setup lang="ts">
import BoosterImage from '#booster/components/BoosterImage.vue'
import type { TProps as TCarouselProps } from './BaseCarousel.vue'

type TProps = { modelName?: string } & Omit<TCarouselProps, 'itemCount'>

const props = withDefaults(defineProps<TProps>(), {
  modelName: 'Fallback Do Not Delete',
})

const { swipeOverflowBarrierColours } = toReactive(props)

const { locale, localeIsoCode } = useLocaleIso()

const { data: headerLinkList, status: headerLinkStatus } = await useFetch(
  () =>
    `/api/testimonials/carousel?locale=${localeIsoCode.value}&name=${props.modelName}`,
  {
    method: 'post',
    default: () => [],
    watch: [locale],
    lazy: true,
    server: false,
    key: `testimonials-carousel-${localeIsoCode.value}-${props.modelName}`,
  }
)

const headerLinkListComputed = computed(() => headerLinkList.value)

const currentSlide = ref<number>(0)
</script>

<template>
  <LazyBaseCarousel
    v-if="headerLinkStatus === 'success' && headerLinkListComputed?.length > 0"
    :item-count="headerLinkList?.length"
    :canvas-height="props.canvasHeight"
    :autoplay="props.autoplay"
    :interval="props.interval"
    :swipe="props.swipe"
    :swipe-threshold="props.swipeThreshold"
    :navigation="props.navigation"
    :tabs="props.tabs"
    :swipe-overflow-barrier-colours="swipeOverflowBarrierColours"
    @update:slide="currentSlide = $event.newSlide"
  >
    <LazyBaseCarouselSlide
      v-for="(item, index) in headerLinkListComputed"
      :key="`carousel-testimonial-${item.author}-${item.testimonal}-${index}`"
      :index="index"
      :current-index="currentSlide"
    >
      <BoosterImage
        v-if="item.image"
        :quality="80"
        :title="`${item.author} ${item.authorTitle} testimonial`"
        placeholder
        :src="item.image"
        :alt="`${item.author} testimonial image`"
        class="w-auto !h-[52px] max-h-[52px] !w-auto object-contain aspect-auto"
        provider="https://cdn.builder.io"
        loading="lazy"
        loading-spinner
      />

      <div
        class="whitespace-pre-wrap text-trail-title4 lg:text-trail-title1 text-trail-text-title-normal"
      >
        {{ item.testimonial }}
      </div>

      <div class="flex justify-center items-center gap-1">
        <div
          class="text-trail-caption font-medium lg:text-trail-title5 text-trail-text-title-normal"
        >
          {{ item.author }}
        </div>

        <div
          class="text-trail-caption lg:text-trail-body3 text-trail-text-body-normal"
        >
          {{ item.authorTitle }}
        </div>
      </div>
    </LazyBaseCarouselSlide>
  </LazyBaseCarousel>

  <BaseSkeleton v-else class="h-44 w-128" />
</template>
